/* eslint-disable @atlaskit/design-system/ensure-design-token-usage */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { MouseEvent } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { useAtomValue } from 'jotai';
import { FormattedMessage } from 'react-intl';

import { getDocument } from '@atlaskit/browser-apis';
import { token } from '@atlaskit/tokens';

import { currentDisplayedTierAtom } from '../../../state';
import { messages } from '../messages';

/**
 * Smoothly scrolls the section with the matching `id` into view, aligning its
 * top with the viewport top (offset by each section's `scroll-margin-top`,
 * which accounts for the fixed `NavigationBar`).
 *
 * Used by every nav button that maps to an in-page section, so the behaviour
 * stays consistent whether the user clicks "About", "Connectors", "CLI", etc.
 */
const scrollToSection = (sectionId: string): void => {
	getDocument()?.getElementById(sectionId)?.scrollIntoView({
		behavior: 'smooth',
		block: 'start',
	});
};

const navigateToSection =
	(sectionId: string): ((event: MouseEvent<HTMLAnchorElement>) => void) =>
	(event) => {
		event.preventDefault();
		scrollToSection(sectionId);
	};

const styles = cssMap({
	centerNav: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: token('space.100'),
		justifySelf: 'center',
		marginBottom: token('space.negative.050'),
		'@media (max-width: 48rem)': {
			display: 'none',
		},
	},
	button: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		border: 'none',
		borderRadius: token('radius.medium'),
		backgroundColor: 'transparent',
		color: '#B7B9BE',
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
});

type NavButtonsProps = {
	ariaLabel: string;
	isLoggedIn: boolean;
};

/**
 * @deprecated remove on FG twg-microsite-enable-standalone-cli-page clean up
 */
export const NavButtons = ({ ariaLabel, isLoggedIn }: NavButtonsProps): JSX.Element => {
	const tier = useAtomValue(currentDisplayedTierAtom);

	const renderNavItem = (sectionId: string, label: JSX.Element): JSX.Element => (
		// eslint-disable-next-line @atlaskit/design-system/no-html-anchor -- in-page hash navigation needs native link semantics
		<a href={`#${sectionId}`} css={styles.button} onClick={navigateToSection(sectionId)}>
			{label}
		</a>
	);

	if (!isLoggedIn) {
		// CLI and MCP both scroll to the new "Connect your agents" section,
		// which renders the CLI and MCP options side-by-side. There's no tab
		// state to flip on the new section, so a plain scroll is enough.
		return (
			<nav css={styles.centerNav} aria-label={ariaLabel}>
				{renderNavItem('overview', <FormattedMessage {...messages.navAbout} />)}
				{renderNavItem('connectors', <FormattedMessage {...messages.navConnectors} />)}
				{renderNavItem('getting-started-v2-cli', <FormattedMessage {...messages.navCli} />)}
				{renderNavItem('getting-started-v2-mcp', <FormattedMessage {...messages.navMcp} />)}
			</nav>
		);
	}

	if (!tier) {
		return <nav css={styles.centerNav} aria-label={ariaLabel}></nav>;
	}

	return (
		<nav css={styles.centerNav} aria-label={ariaLabel}>
			{tier !== 'tier3' &&
				renderNavItem('graph-preview', <FormattedMessage {...messages.navYourGraph} />)}
			{renderNavItem('connectors', <FormattedMessage {...messages.navConnectors} />)}
			{renderNavItem('getting-started-v2-cli', <FormattedMessage {...messages.navCli} />)}
			{renderNavItem('getting-started-v2-mcp', <FormattedMessage {...messages.navMcp} />)}
		</nav>
	);
};
